import { z } from "zod";
import { tickRateHzSchema } from "./product.js";
export const wovenIdSchema = z
    .string()
    .refine((v) => /^[1-9][0-9]{0,19}$/.test(v) && BigInt(v) <= 18446744073709551615n);
export const MAX_SPATIAL_SUBSPACES = 64;
export const DEFAULT_SPATIAL_SUBSPACE = {
    metersPerUnit: 1,
    cellSize: 10,
    interestRadius: 25,
    exactDistance: true,
    bounds: {
        min: { x: -1000, y: -100, z: -1000 },
        max: { x: 1000, y: 500, z: 1000 },
    },
};
const finiteNumberSchema = z.number().finite();
export const spatialPoint3Schema = z
    .object({ x: finiteNumberSchema, y: finiteNumberSchema, z: finiteNumberSchema })
    .strict();
export const spatialBoundsSchema = z
    .object({ min: spatialPoint3Schema, max: spatialPoint3Schema })
    .strict()
    .refine(({ min, max }) => min.x < max.x && min.y < max.y && min.z < max.z, "bounds min must be less than max on every axis");
const positiveFiniteNumberSchema = finiteNumberSchema.positive();
const spatialConfigurationShape = {
    metersPerUnit: positiveFiniteNumberSchema,
    cellSize: positiveFiniteNumberSchema,
    interestRadius: positiveFiniteNumberSchema,
    exactDistance: z.boolean(),
    bounds: spatialBoundsSchema,
};
export const spatialSubspaceConfigurationSchema = z.object(spatialConfigurationShape).strict();
export const spatialSubspaceSlugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,63})$/);
export const spatialSubspaceDisplayNameSchema = z.string().trim().min(1).max(64);
export const spatialSpaceIdSchema = wovenIdSchema.refine((value) => BigInt(value) >= 3n);
const managedChannelIdsSchema = z.tuple([z.literal("1"), z.literal("4")]);
const logicalSpaceSchema = (spaceId) => z
    .object({
    kind: z.literal("logical"),
    spaceId: z.literal(spaceId),
    epoch: z.literal("1"),
    channelIds: managedChannelIdsSchema,
})
    .strict();
export const managedLogicalSpaceSchema = z.union([
    logicalSpaceSchema("1"),
    logicalSpaceSchema("2"),
]);
export const spatialSubspaceDefinitionSchema = z
    .object({
    kind: z.literal("spatialGrid3D"),
    slug: spatialSubspaceSlugSchema,
    displayName: spatialSubspaceDisplayNameSchema,
    spaceId: spatialSpaceIdSchema,
    epoch: z.literal("1"),
    channelIds: managedChannelIdsSchema,
    ...spatialConfigurationShape,
})
    .strict();
export const managedSpacesSchema = z
    .tuple([logicalSpaceSchema("1"), logicalSpaceSchema("2")])
    .rest(spatialSubspaceDefinitionSchema)
    .refine((spaces) => spaces.length <= MAX_SPATIAL_SUBSPACES + 2, "too many spatial subspaces")
    .refine((spaces) => new Set(spaces.map((space) => space.spaceId)).size === spaces.length, "duplicate space ID")
    .refine((spaces) => new Set(spaces.filter((space) => space.kind === "spatialGrid3D").map((space) => space.slug))
    .size ===
    spaces.length - 2, "duplicate spatial subspace slug");
export const serverConnectionStatusSchema = z.object({
    tokenStatus: z.enum(["unavailable", "active"]),
    endpoint: z.string().optional(),
    namespaceId: wovenIdSchema.optional(),
    sessionId: wovenIdSchema.optional(),
});
const webTransportUrlPattern = /^https:\/\/(\[[0-9a-f:.]+\]|(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)*[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?):([1-9][0-9]{0,4})(\/[A-Za-z0-9._~!$&'()*+,;=:@/-]*)$/;
function isCanonicalNumericHost(hostname) {
    if (hostname.startsWith("[")) {
        const source = hostname.slice(1, -1);
        const halves = source.split("::");
        if (halves.length > 2)
            return false;
        const left = halves[0] ? halves[0].split(":") : [];
        const right = halves[1] ? halves[1].split(":") : [];
        if ([...left, ...right].some((group) => !/^[0-9a-f]{1,4}$/.test(group)))
            return false;
        const missing = 8 - left.length - right.length;
        if ((halves.length === 1 && missing !== 0) || (halves.length === 2 && missing < 1))
            return false;
        const groups = [
            ...left.map((group) => Number.parseInt(group, 16)),
            ...Array.from({ length: missing }, () => 0),
            ...right.map((group) => Number.parseInt(group, 16)),
        ];
        let bestStart = -1;
        let bestLength = 1;
        for (let start = 0; start < groups.length;) {
            if (groups[start] !== 0) {
                start += 1;
                continue;
            }
            let end = start;
            while (end < groups.length && groups[end] === 0)
                end += 1;
            if (end - start > bestLength) {
                bestStart = start;
                bestLength = end - start;
            }
            start = end;
        }
        const normalized = groups.map((group) => group.toString(16));
        const canonical = bestStart < 0
            ? normalized.join(":")
            : `${normalized.slice(0, bestStart).join(":")}::${normalized
                .slice(bestStart + bestLength)
                .join(":")}`;
        return source === canonical;
    }
    if (!/^\d+(?:\.\d+){3}$/.test(hostname))
        return true;
    return hostname.split(".").every((part) => Number(part) <= 255 && String(Number(part)) === part);
}
export const webTransportUrlSchema = z
    .string()
    .refine((value) => {
    if (/\s|%|\\/.test(value))
        return false;
    const match = webTransportUrlPattern.exec(value);
    return (match != null &&
        Number(match[2]) <= 65_535 &&
        isCanonicalNumericHost(match[1]) &&
        !match[3].split("/").some((segment) => segment === "." || segment === ".."));
}, "must be a canonical HTTPS URL with an explicit valid port and path, without normalization, credentials, query, fragment or whitespace")
    .pipe(z.string().url());
export const serverConnectionSchema = z
    .object({
    endpoint: z.string(),
    transport: z.literal("quic"),
    namespaceId: wovenIdSchema,
    sessionId: wovenIdSchema,
    spaces: managedSpacesSchema,
    token: z.string().regex(/^[0-9a-f]{64}$/),
    caPem: z.string().optional(),
    webTransport: z
        .object({
        url: webTransportUrlSchema,
        certificateSha256: z
            .string()
            .regex(/^[0-9a-f]{64}$/)
            .optional(),
    })
        .strict()
        .optional(),
})
    .strict();
export const serverAdmissionSchema = z
    .object({
    effectiveAllocatedCCU: z.number().int().nonnegative(),
    pendingTarget: z.number().int().nonnegative().nullable(),
    activeCCU: z.number().int().nonnegative(),
    offeredSlots: z.number().int().nonnegative(),
    queueDepth: z.number().int().nonnegative(),
    availableSlots: z.number().int().nonnegative(),
})
    .strict();
export const serverMonitoringSchema = z
    .object({
    nodeIncarnation: z.string().min(1),
    namespaceId: wovenIdSchema,
    sessionId: wovenIdSchema,
    revision: wovenIdSchema,
    allocatedCCU: z.number().int().nonnegative(),
    tickRateHz: tickRateHzSchema.optional(),
    admission: serverAdmissionSchema,
    spaces: managedSpacesSchema,
})
    .strict();
export const serverGenerationSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
export const serverGenerationRequestSchema = z
    .object({ generation: serverGenerationSchema })
    .strict();
export const recoverServerRequestSchema = serverGenerationRequestSchema
    .extend({ confirmation: z.literal("RECOVER") })
    .strict();
export const provisionServerRequestSchema = serverGenerationRequestSchema;
export const serverConnectionRequestSchema = serverGenerationRequestSchema;
export const serverMonitoringQuerySchema = serverGenerationRequestSchema;
export const listSpatialSubspacesQuerySchema = serverGenerationRequestSchema;
export const addSpatialSubspaceRequestSchema = serverGenerationRequestSchema
    .extend({
    slug: spatialSubspaceSlugSchema,
    displayName: spatialSubspaceDisplayNameSchema,
    metersPerUnit: positiveFiniteNumberSchema.default(DEFAULT_SPATIAL_SUBSPACE.metersPerUnit),
    cellSize: positiveFiniteNumberSchema.default(DEFAULT_SPATIAL_SUBSPACE.cellSize),
    interestRadius: positiveFiniteNumberSchema.default(DEFAULT_SPATIAL_SUBSPACE.interestRadius),
    exactDistance: z.boolean().default(DEFAULT_SPATIAL_SUBSPACE.exactDistance),
    bounds: spatialBoundsSchema.default(DEFAULT_SPATIAL_SUBSPACE.bounds),
})
    .strict();
export const spatialSubspacesResponseSchema = z
    .object({
    generation: serverGenerationSchema,
    revision: wovenIdSchema,
    maxSpatialSubspaces: z.literal(MAX_SPATIAL_SUBSPACES),
    spatialSubspaceCount: z.number().int().min(0).max(MAX_SPATIAL_SUBSPACES),
    spaces: managedSpacesSchema,
})
    .strict()
    .refine(({ spatialSubspaceCount, spaces }) => spatialSubspaceCount === spaces.length - 2, "spatial subspace count does not match definitions");
export const deleteServerRequestSchema = serverGenerationRequestSchema
    .extend({ confirmation: z.literal("DELETE") })
    .strict();
export const deleteServerResponseSchema = z.object({ deleted: z.literal(true) }).strict();
export const deleteRecoveryRequestSchema = deleteServerRequestSchema;
export const deleteRecoveryResponseSchema = deleteServerResponseSchema;
//# sourceMappingURL=server-connection.js.map